import { FunctionComponent, useEffect, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import styles from './addChildToParentDialog.module.scss'
import { useTranslation } from 'react-i18next'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique'
import { OptionsGroupDeprecated, SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select'
import { ATTACH_CHILD_TO_PARENT, GET_PARENT_LIST } from '@/modules/API/parent/parentHandler'
import { Parent } from '@/types/Parent'
import { Child } from '@/types/Child'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage'
import Loading from '@/components/Loading/Loading'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface AddChildToParentDialogProps {
    open: boolean
    handleClose: () => void
    parentId: number
    daycareId: number
    parentName: string
    handleRefetchParent: () => Promise<void>
}

const AddChildToParentDialog: FunctionComponent<AddChildToParentDialogProps> = ({
    open = false,
    handleClose,
    parentId,
    daycareId,
    parentName,
    handleRefetchParent
}) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [actualParentChildIds, setActualParentChildIds] = useState<number[]>([])

    const [parentList, setParentList] = useState<Parent[]>([])
    const [selectedChildId, setSelectedChildId] = useState<string>('0')

    const {
        loading: loadingGetParents,
        data: dataGetParents,
        error: errorGetParents
    } = useQuery(GET_PARENT_LIST, {
        variables: {
            daycare_id: daycareId
        }
    })

    useEffect(() => {
        if (dataGetParents) {
            const parent: Parent | undefined = dataGetParents.getParentList.find(
                (parent: Parent) => parent.id === parentId
            )
            const list: Parent[] = dataGetParents.getParentList.filter((parent: Parent) => parent.id !== parentId)

            setActualParentChildIds(parent ? parent.childs.map((child: Child) => child.id) : [])
            setParentList(list)
        }
    }, [dataGetParents])

    useEffect(() => {
        if (errorGetParents) {
            handleClose()
        }
    }, [errorGetParents])

    const [daycareAttachChild] = useMutation(ATTACH_CHILD_TO_PARENT, {
        variables: {
            parentId: parentId,
            daycareId: daycareId,
            childId: Number(selectedChildId)
        },
        onCompleted: () => {
            handleRefetchParent()

            showNotificationSuccess(t('childAdded'))
            handleClose()
        },
        onError: () => {
            showNotificationError(t('childAddedError'))
            handleClose()
        }
    })

    const selectOptions: SelectOptionsDeprecated = parentList
        .sort((parentA: Parent, parentB: Parent): number => parentA.email.localeCompare(parentB.email))
        .sort((parentA: Parent, parentB: Parent): number => parentA.lastname.localeCompare(parentB.lastname))
        .map((parent: Parent): OptionsGroupDeprecated | undefined => {
            const parentChilds: Child[] = parent.childs.filter(
                (child: Child): boolean => !actualParentChildIds.includes(child.id)
            )
            if (parentChilds.length > 0) {
                return {
                    name: parent.firstname + ' ' + parent.lastname,
                    options: parentChilds.map((child: Child): SelectOptionDeprecated => {
                        return {
                            value: child.id.toString(),
                            name: child.firstname
                        }
                    })
                }
            }
        })
        .filter(
            (option: OptionsGroupDeprecated | undefined): boolean => option !== undefined
        ) as OptionsGroupDeprecated[]

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t('addChild')} />
            <DialogBody className={styles.DialogBody}>
                <p className={styles.Text}>
                    {t('whichChildYouWantToAdd')}
                    <span className={styles.TextFamily}>{parentName}</span>
                    {' ?'}
                </p>
                <p className={styles.Text}>{t('noRollBack')}</p>
                {loadingGetParents && <Loading overall={false} />}
                {errorGetParents && <ErrorMessage error={t('noChild')} />}
                {dataGetParents && (
                    <>
                        {selectOptions.length > 0 ? (
                            <div className={styles.InputContainer}>
                                <SelectUnique
                                    selectOptions={selectOptions}
                                    selectValue={selectedChildId}
                                    setSelectValue={setSelectedChildId}
                                    className={styles.Select}
                                    label={t('childs')}
                                />
                            </div>
                        ) : (
                            <ErrorMessage className={styles.Text} error={t('noChild')} />
                        )}
                        <div className={styles.ButtonContainer}>
                            <ButtonTextIcon
                                type={'add'}
                                onClick={daycareAttachChild}
                                className={styles.Button}
                                disabled={selectedChildId === '0'}
                            />
                            <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                        </div>
                    </>
                )}
            </DialogBody>
        </Dialog>
    )
}

export default AddChildToParentDialog
